import React from 'react'
import filter from 'lodash/filter'
import find from 'lodash/find'
import * as TWEEN from 'tween.js'

import { CHOOSE_FIRST_STEP_NODE, NodeType } from 'common/builder/constants/NodeType'
import { isTestEnv } from 'utils/e2e'
import errorTrackingService from 'utils/services/errorTrackingService'

import app from './app'
import config from './config'
import { getNodeTargets } from './utils'

const POSITION_DURATION = 700

const getBottomBound = (levels, from) => {
  let bottom = 0
  for (let i = from; i < levels.length; i++) {
    if (levels[i].bottom >= bottom) {
      bottom = levels[i].bottom
    }
  }
  return bottom
}

const getOffsetLeft = (levels, to) => {
  let res = 0
  for (let i = 0; i < to; i++) {
    res += levels[i].width
  }
  return res
}

export default class PositionManager extends React.Component {
  componentDidMount() {
    const { noPosition } = this.props
    const immediately = Boolean(this.props.immediately || isTestEnv())
    app.registerRearrangeHandler(this.autoPosition)

    if (noPosition) {
      return
    }

    const bounds = this.autoPosition(false, immediately)

    const width = bounds.right - bounds.left
    const height = bounds.top - bounds.bottom
    const centerX = bounds.left + width / 2
    const centerY = bounds.bottom + height / 2

    if (immediately) {
      if (app.initialFrameNodeId) {
        return app.moveTo(app.initialFrameNodeId)
      }
      return app.moveToFrame(centerX, centerY, width, height, immediately)
    }

    setTimeout(() => {
      if (app.initialFrameNodeId) {
        return app.moveTo(app.initialFrameNodeId)
      }
      app.moveToFrame(centerX, centerY, width, height)
    }, 100)
  }

  autoPosition = (ignorePrev, instant) => {
    const { rootId, coordinates, nodes, blocks, buttons } = this.props // eslint-disable-line no-unused-vars

    const levels = [],
      positioned = [],
      margin = 100,
      levelWidth = config.CONTENT_WIDTH + config.CONTENT_POSITION_RIGHT_MARGIN

    const bounds = {
      top: Number.MIN_SAFE_INTEGER,
      right: Number.MIN_SAFE_INTEGER,
      bottom: Number.MAX_SAFE_INTEGER,
      left: Number.MAX_SAFE_INTEGER,
    }

    const updateBounds = (x, y, width, height) => {
      if (y + height > bounds.top) {
        bounds.top = y + height
      }
      if (x + width > bounds.right) {
        bounds.right = x + width
      }
      if (y < bounds.bottom) {
        bounds.bottom = y
      }
      if (x < bounds.left) {
        bounds.left = x
      }
    }

    const setPosition = ($obj, x, y) => {
      if (instant) {
        $obj.position.set(x, y)
      } else {
        $obj.fx({ x, y }, { duration: POSITION_DURATION, easing: TWEEN.Easing.Quadratic.InOut })
      }
    }

    const pos = ({ id }, level, parentY = 0) => {
      // Fixing target with 'contentId'
      if (Number.isFinite(id)) {
        const c = Object.values(nodes).find((c) => c.contentId === id)
        if (c == null) {
          return
        }
        id = c.id
      }

      // Preventing circular positioning
      if (positioned.includes(id)) {
        return
      }
      positioned.push(id)

      if (levels[level] == null) {
        levels[level] = {
          bottom: 0,
          width: levelWidth,
        }
      }

      const $g = app.getNode(id)
      if ($g == null) {
        return console.log('POSITION ERROR: canvasObject not found, id:', id)
      }

      const newX = getOffsetLeft(levels, level)
      const newY = Math.max(getBottomBound(levels, level), parentY)
      if (ignorePrev || !coordinates[id]) {
        setPosition($g, newX, newY)

        levels[level].bottom = newY + $g.getHeight() + margin

        const _width = $g.getWidth() + config.CONTENT_POSITION_RIGHT_MARGIN
        levels[level].width = Math.max(levels[level].width, _width)

        updateBounds(newX, newY, $g.getWidth(), $g.getHeight())
      } else {
        updateBounds($g.getX(), $g.getY(), $g.getWidth(), $g.getHeight())
      }

      let el = nodes[id]
      if (!el) {
        return
      }

      let targets = getNodeTargets(el, { blocks, buttons, nodes })
      for (let t of targets) {
        pos(t, level + 1, newY)
      }
    }

    // Starging step position
    const startingStepNode = find(nodes, { isStartingStep: true })
    const $startingStepNodeElemnt = app.getNode(startingStepNode?.id)
    if (startingStepNode) {
      const currentStartingStepCoords = coordinates[startingStepNode.id]

      if ($startingStepNodeElemnt == null) {
        errorTrackingService.trackError('POSITION ERROR: canvasObject not found', {
          extra: { id: startingStepNode.id },
          fingerprint: 'position-manager-error',
          tags: { area: 'builder', topic: "id doesn't exist" },
        })
        console.log('POSITION ERROR: canvasObject not found, id:', startingStepNode.id)
      } else if (!currentStartingStepCoords || ignorePrev) {
        const startingStepPositionXOffset =
          config.STARTING_STEP_NODE_WIDTH + config.CONTENT_POSITION_RIGHT_MARGIN
        const startingStepX = coordinates[rootId]?.x || 0 - startingStepPositionXOffset
        const startingStepY = coordinates[rootId]?.y || 0

        setPosition($startingStepNodeElemnt, startingStepX, startingStepY)
        updateBounds(startingStepX, 0, config.STARTING_STEP_NODE_WIDTH, startingStepY)
      } else {
        updateBounds(
          $startingStepNodeElemnt.getX(),
          $startingStepNodeElemnt.getY(),
          $startingStepNodeElemnt.getWidth(),
          $startingStepNodeElemnt.getHeight(),
        )
      }
    }

    // Steps position
    const $chooseFirstStepElement = app.getNode(CHOOSE_FIRST_STEP_NODE)
    if ($chooseFirstStepElement) {
      pos({ id: CHOOSE_FIRST_STEP_NODE }, 0)
    } else if ($startingStepNodeElemnt) {
      pos({ id: NodeType.STARTING_STEP }, 0)
    } else if (rootId) {
      pos({ id: rootId }, 0)
    }

    // add notes to bounds
    filter(this.props.nodes, { nodeType: 'comment' }).forEach((node) => {
      const $g = app.getNode(node.id)
      if ($g) {
        updateBounds($g.getX(), $g.getY(), $g.getWidth(), $g.getHeight())
      }
    })

    return bounds
  }

  render() {
    return null
  }
}
